import React, { HTMLAttributes, useCallback, useMemo, useRef } from 'react';
import { useMountEffect } from '@direct-frontend/stdlib';

import {
    getOnOffAutoComplete,
    isComboboxStateVirtualized,
    isComboboxVirtualized,
    useComboboxProps,
} from '../../../Combobox/utils';
import { useVStack } from '../../../../hooks/useStack';
import { ListBox } from '../../../ListBox/ListBox';
import { ListBoxVirtualized } from '../../../ListBox/ListBoxVirtualized';
import { List } from '../../../List';
import { Drawer, DrawerHeader } from '../../../Drawer';
import { TagsWrapper } from '../TagsWrapper/TagsWrapper';
import { cn } from '../../../../lib/css/className/className';
import { ComboboxProps, InputRenderer } from '../../../Combobox';
import { TagTextInput } from '../TagTextInput/TagTextInput';
import { getSizeSkin } from '../../../../lib/skin/getSizeSkin/getSizeSkin';

import type { TagComboboxEditorProps } from './types';

const cls = cn('TagComboboxEditor');

const defaultInputRenderer: InputRenderer = props => <TagTextInput {...props} />;

export type TagComboboxEditorMobileProps<ValueType extends string, DataT = unknown> = TagComboboxEditorProps<
    ValueType,
    DataT
> &
    Pick<ComboboxProps<ValueType>, 'onSelectionChange'>;

export const TagComboboxEditor = function TagComboboxEditor<ValueType extends string, DataT = unknown>(
    props: TagComboboxEditorMobileProps<ValueType, DataT>,
) {
    const {
        children = defaultInputRenderer,
        displaySelection = true,
        label,
        pinnedSectionAfter: propsPinnedSectionAfter,
        emptySection: propsEmptySection,
        'data-testid': dataTestId = 'Combobox',
        tagGroupSize,
        tags,
        onChange,
        onCustomValueAdd,
        onCustomValueEdit,
        onRemove,
        onReplaceTags,
        initialTags,
        inputValue,
        setSelectedTagIndex,
        selectedTagIndex,
        clearTagIndex,
    } = props;
    const stackClass = useVStack({ gap: '16' });
    const sizeSkinClass = getSizeSkin(tagGroupSize ?? 'm');

    const fallbackRef = useRef<HTMLInputElement>(null);
    const { LogProvider, state, listBoxRef, inputRef, popoverRef, inputProps, listBoxProps } = useComboboxProps(
        {
            ...props,
            menuTrigger: 'manual',
            shouldCloseOnBlur: false,
            allowsEmptyCollection: true,
            selectedKey: null,
            onInputChange: onChange,
            onOpenChange: isOpen => {
                if (!isOpen) {
                    props.onBlur?.();
                }
            },
        },
        fallbackRef,
    );

    useMountEffect(() => {
        state.open('first', 'manual');
    });

    const externalInputProps: Omit<HTMLAttributes<HTMLInputElement>, 'color' | 'onChange'> = {
        role: inputProps.role,
        'aria-expanded': inputProps['aria-expanded'],
        'aria-controls': inputProps['aria-controls'],
        'aria-autocomplete': inputProps['aria-autocomplete'],
        'aria-activedescendant': inputProps['aria-activedescendant'],
        autoCorrect: inputProps.autoCorrect,
        spellCheck: inputProps.spellCheck,
    };

    const pinnedSectionAfter = useMemo(
        () =>
            typeof propsPinnedSectionAfter === 'function'
                ? propsPinnedSectionAfter(state.close)
                : propsPinnedSectionAfter,
        [propsPinnedSectionAfter, state.close],
    );

    const emptySection = useMemo(
        () => (typeof propsEmptySection === 'function' ? propsEmptySection(state.close) : propsEmptySection),
        [propsEmptySection, state.close],
    );

    const onSaveClick = useCallback(() => {
        if ((onCustomValueAdd || onCustomValueEdit) && inputValue) {
            const trimmedValue = inputValue?.trim();
            if (trimmedValue && trimmedValue.length !== 0) {
                if (selectedTagIndex === -1 || selectedTagIndex === undefined) {
                    onCustomValueAdd?.(trimmedValue);
                } else {
                    onCustomValueEdit?.(trimmedValue, selectedTagIndex);
                    clearTagIndex?.();
                }

                onChange('');
            }
        }

        state.close();
    }, [clearTagIndex, inputValue, onChange, onCustomValueAdd, onCustomValueEdit, selectedTagIndex, state]);

    const onTagSelect = useCallback(
        (index: number) => {
            setSelectedTagIndex?.(index);
            const tag = tags?.[index];
            const tagValue = tag?.text ?? (typeof tag?.tag === 'string' ? tag.tag : '');
            onChange(tagValue);
        },
        [onChange, setSelectedTagIndex, tags],
    );

    const handleCancelClick = useMemo(() => {
        if (!onReplaceTags || !initialTags) {
            return undefined;
        }

        return () => {
            state.close();
            onReplaceTags(initialTags);
        };
    }, [onReplaceTags, initialTags, state]);

    let drawerContent = null;

    if (state.collection.size === 0 && emptySection) {
        drawerContent = (
            <List size={'m'} key="empty" width={'max'}>
                {emptySection}
            </List>
        );
    } else if (isComboboxStateVirtualized(props, state) && isComboboxVirtualized(props)) {
        drawerContent = (
            <ListBoxVirtualized
                {...listBoxProps}
                selectionMode={'single'}
                ref={listBoxRef}
                state={state}
                size={'m'}
                items={props.items}
                data-testid={`${dataTestId}.listBox`}
                displaySelection={displaySelection}
                pinnedSection={props.pinnedSection}
                pinnedSectionAfter={pinnedSectionAfter}
            />
        );
    } else {
        drawerContent = (
            <ListBox
                {...listBoxProps}
                selectionMode={'single'}
                ref={listBoxRef}
                state={state}
                size={'m'}
                items={props.items}
                data-testid={`${dataTestId}.listBox`}
                displaySelection={displaySelection}
                pinnedSection={props.pinnedSection}
                pinnedSectionAfter={pinnedSectionAfter}
            />
        );
    }

    const editorInput = children({
        ...props,
        ...inputProps,
        ...externalInputProps,
        color: 'contrast',
        autoFocus: true,
        onBlur: undefined,
        onChange: state.setInputValue,
        onKeyDown: props.onKeyDown,
        size: tagGroupSize,
        value: String(inputProps.value),
        ref: inputRef,
        inDrawer: true,
        'data-testid': `${dataTestId}.Textinput`,
        autoComplete: getOnOffAutoComplete(inputProps.autoComplete),
        spellCheck: String(inputProps.spellCheck) === 'true',
    });

    return (
        <LogProvider>
            <div className={cls('pseudoInput', [props.className, sizeSkinClass])} />
            <Drawer
                ref={popoverRef}
                className={stackClass}
                isOpen={state.isOpen}
                onClose={state.close}
                header={
                    <DrawerHeader
                        title={label}
                        onCancelClick={handleCancelClick}
                        onSaveClick={onSaveClick}
                        data-testid={`${dataTestId}.Header`}
                    />
                }
                data-testid={`${dataTestId}.Drawer`}
                isFullscreen
            >
                <TagsWrapper
                    tags={tags ?? []}
                    onRemove={onRemove}
                    onClick={onTagSelect}
                    selectedTagIndex={selectedTagIndex}
                    editor={editorInput}
                    data-testid={`${dataTestId}.Drawer.Field`}
                >
                    {selectedTagIndex === -1 && editorInput}
                </TagsWrapper>
                {drawerContent}
            </Drawer>
        </LogProvider>
    );
};
